import {
    ArrowLeft,
    Globe,
    ImageIcon,
    Layers,
    Package,
    Pencil,
    Plus,
    Scissors,
    Store,
    Trash2,
    UserRound,
    Wallet,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from '../../../assets/scss/pages/ServiceDetail.module.scss';
import api from '../../../config/axios-conf';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency } from '../../../utils/formatters';

const TYPE_LABELS = { dichvu: 'Dịch vụ', sanpham: 'Sản phẩm', combo: 'Combo' };
const TYPE_ICONS = { dichvu: <Scissors size={14} />, sanpham: <Package size={14} />, combo: <Layers size={14} /> };

function getVariantDisplayName(variant) {
    if (variant?.attributeValues?.length > 0) {
        return variant.attributeValues.map((v) => v.value).join(' - ');
    }
    return variant?.sku || 'Mặc định';
}

function getVariantAttributeText(variant) {
    if (!variant?.attributeValues?.length) return 'Chưa có';
    return variant.attributeValues.map((attr) => `${attr.attribute?.name || ''}: ${attr.value || ''}`).join(', ');
}

function getItemPrice(variant) {
    return (
        (variant?.salePrice && Number(variant.salePrice) > 1000
            ? Number(variant.salePrice)
            : Number(variant?.retailPrice)) || 0
    );
}

export default function ServiceDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canUpdate = hasPermission(PERMISSIONS.SERVICE.UPDATE);
    const canDelete = hasPermission(PERMISSIONS.SERVICE.DELETE);
    const canCreate = hasPermission(PERMISSIONS.SERVICE.CREATE);

    const [serviceData, setServiceData] = useState(null);
    const [variants, setVariants] = useState([]);
    const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
    const [comboItems, setComboItems] = useState([]);
    const [commissionTypes, setCommissionTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
    const [toastMsg, setToastMsg] = useState(null); // { type: 'success' | 'error', text }

    // Chế độ xem: 'owner' (chủ — xem đầy đủ) | 'guest' (khách — ẩn giá nhập, tồn kho; bấm ảnh để phóng to)
    const [viewMode, setViewMode] = useState('guest');
    const [showImagePreview, setShowImagePreview] = useState(false);
    const isGuestView = viewMode === 'guest';

    // Modal cập nhật hoa hồng — giống app
    const [showCommissionModal, setShowCommissionModal] = useState(false);
    const [selectedCommissionId, setSelectedCommissionId] = useState(null); // number | 'none' | null
    const [applyToAllVariants, setApplyToAllVariants] = useState(false);
    const [commissionSubmitting, setCommissionSubmitting] = useState(false);

    // Lấy tên hoa hồng từ commissionTypeId
    const getCommissionDisplayName = useCallback(
        (commissionTypeId) => {
            if (!commissionTypeId) return 'Chưa có';
            const commission = commissionTypes.find((c) => c.id === commissionTypeId);
            if (!commission) return 'Chưa có';
            return `${commission.name} (${commission.type === 'percent' ? `${Number(commission.value)}%` : formatCurrency(Number(commission.value))})`;
        },
        [commissionTypes],
    );

    // Fetch service + variants + commissions
    const fetchAll = useCallback(
        async (silent = false) => {
            if (!silent) setLoading(true);
            try {
                // Fetch service (response.data.variant là service object, không phải variant)
                const { data } = await api.get(`/services/variant/${id}`);
                const service = data.variant || data;

                setServiceData({
                    code: service.code || '',
                    name: service.name || '',
                    price: Math.round(parseFloat(service.price || 0)),
                    typeService: service.typeService || '',
                    urlImg: service.urlImg || '',
                    description: service.description || '',
                    displayLocation: service.displayLocation || '',
                    internalNote: service.internalNote || '',
                    isWeb: service.isWeb || false,
                });
                setVariants(service.variants || []);
                setSelectedVariantIdx(0);

                // Fetch commissions
                try {
                    const { data: commData } = await api.get('/services/commissionAll');
                    setCommissionTypes(commData.commissions || []);
                } catch {
                    /* ignore */
                }

                // Fetch combo items nếu là combo
                if (service.typeService === 'combo') {
                    try {
                        const { data: detailData } = await api.get(`/services/detail/${id}`);
                        const detail = detailData.service || detailData;
                        setComboItems(
                            (detail.comboItems || []).map((ci) => ({
                                id: ci.id,
                                variantId: ci.variantId,
                                quantity: ci.quantity,
                                serviceName: ci.variant?.service?.name || '',
                                variantName: ci.variant?.attributeValues?.map((v) => v.value).join(' - ') || '',
                                price: ci.variant?.salePrice > 0 ? ci.variant.salePrice : ci.variant?.retailPrice || 0,
                            })),
                        );
                    } catch {
                        setComboItems([]);
                    }
                }
            } catch (err) {
                console.error('Error fetching service:', err);
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [id],
    );

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // Đóng ảnh phóng to bằng phím Esc
    useEffect(() => {
        if (!showImagePreview) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setShowImagePreview(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [showImagePreview]);

    const handleDeleteVariant = async (variantId) => {
        if (!window.confirm('Bạn có chắc muốn xoá biến thể này?')) return;
        try {
            await api.delete(`/services/variant/${variantId}`);
            setToastMsg({ type: 'success', text: 'Xoá biến thể thành công' });
            setTimeout(() => {
                setToastMsg(null);
                fetchAll(true);
            }, 1500);
        } catch (err) {
            const msg = err.response?.data?.message || 'Xoá biến thể thất bại';
            setToastMsg({ type: 'error', text: msg });
            setTimeout(() => setToastMsg(null), 2500);
        }
    };

    const openCommissionModal = () => {
        setSelectedCommissionId(selectedVariant?.commissionTypeId || 'none');
        setApplyToAllVariants(false);
        setShowCommissionModal(true);
    };

    const closeCommissionModal = () => {
        setShowCommissionModal(false);
        setSelectedCommissionId(null);
        setApplyToAllVariants(false);
    };

    const handleUpdateCommission = async () => {
        if (!canUpdate) {
            setToastMsg({ type: 'error', text: 'Bạn không có quyền sửa sản phẩm' });
            setTimeout(() => setToastMsg(null), 2500);
            return;
        }
        if (selectedCommissionId === null) {
            setToastMsg({ type: 'error', text: 'Vui lòng chọn loại hoa hồng!' });
            setTimeout(() => setToastMsg(null), 2500);
            return;
        }

        const commissionTypeId = selectedCommissionId === 'none' ? null : selectedCommissionId;
        setCommissionSubmitting(true);
        try {
            if (applyToAllVariants) {
                await api.put(`/services/variant/commission/${id}`, { commissionTypeId });
            } else {
                await api.put(`/services/variant/${selectedVariant.id}/commission`, { commissionTypeId });
            }
            closeCommissionModal();
            setToastMsg({
                type: 'success',
                text: applyToAllVariants
                    ? 'Đã cập nhật hoa hồng cho tất cả biến thể'
                    : 'Đã cập nhật hoa hồng cho biến thể',
            });
            setTimeout(() => setToastMsg(null), 1800);
            await fetchAll(true);
        } catch (err) {
            const msg = err.response?.data?.message || 'Cập nhật hoa hồng thất bại';
            setToastMsg({ type: 'error', text: msg });
            setTimeout(() => setToastMsg(null), 2500);
        } finally {
            setCommissionSubmitting(false);
        }
    };

    const handleDeleteService = async () => {
        if (!window.confirm('Bạn có chắc muốn xoá?')) return;
        try {
            await api.delete(`/services/${id}`);
            setShowDeleteSuccess(true);
            setTimeout(() => navigate(`/admin/services?tab=${serviceData.typeService}`, { replace: true }), 1200);
        } catch {
            alert('Xoá thất bại');
        }
    };

    // Skeleton loading
    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <div className={styles.skeleton} style={{ width: 40, height: 40, borderRadius: 10 }} />
                        <div className={styles.skeleton} style={{ width: 200, height: 28, borderRadius: 8 }} />
                    </div>
                </div>
                <div className={styles.layout}>
                    <div className={styles.layoutLeft}>
                        <div className={styles.card}>
                            <div className={styles.cardBody}>
                                <div style={{ display: 'flex', gap: 20 }}>
                                    <div
                                        className={styles.skeleton}
                                        style={{ width: 100, height: 100, borderRadius: 14 }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: '60%', height: 24, borderRadius: 8, marginBottom: 10 }}
                                        />
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: '30%', height: 16, borderRadius: 6, marginBottom: 10 }}
                                        />
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            {[80, 60, 70].map((w, i) => (
                                                <div
                                                    key={i}
                                                    className={styles.skeleton}
                                                    style={{ width: w, height: 28, borderRadius: 14 }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.skeleton} style={{ width: 140, height: 20, borderRadius: 6 }} />
                            </div>
                            <div className={styles.cardBody}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                                    {[1, 2, 3].map((i) => (
                                        <div
                                            key={i}
                                            className={styles.skeleton}
                                            style={{ height: 70, borderRadius: 12 }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.layoutRight}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.skeleton} style={{ width: 120, height: 20, borderRadius: 6 }} />
                            </div>
                            <div className={styles.cardBody}>
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} style={{ marginBottom: 16 }}>
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: '40%', height: 14, borderRadius: 6, marginBottom: 6 }}
                                        />
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: '70%', height: 18, borderRadius: 6 }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!serviceData) {
        return (
            <div className={styles.page} style={{ textAlign: 'center', padding: 60, color: '#6B7280' }}>
                Không tìm thấy
            </div>
        );
    }

    const selectedVariant = variants[selectedVariantIdx] || variants[0];
    // Hoa hồng sắp xếp mới nhất trước (theo createdAt, dự phòng theo id)
    const sortedCommissions = [...commissionTypes].sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta || (b.id || 0) - (a.id || 0);
    });
    const isCombo = serviceData.typeService === 'combo';
    const isProduct = serviceData.typeService === 'sanpham';
    const isDichvu = serviceData.typeService === 'dichvu';
    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <h1 className={styles.title}>Chi tiết {TYPE_LABELS[serviceData.typeService] || ''}</h1>
                </div>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        className={`${styles.viewModeBtn}${isGuestView ? ` ${styles.viewModeBtnGuest}` : ''}`}
                        onClick={() => setViewMode(isGuestView ? 'owner' : 'guest')}
                        title={
                            isGuestView
                                ? 'Đang ở chế độ khách — bấm để về chế độ chủ'
                                : 'Đang ở chế độ chủ — bấm để chuyển sang chế độ khách'
                        }
                        aria-label={isGuestView ? 'Chuyển sang chế độ chủ' : 'Chuyển sang chế độ khách'}
                    >
                        {isGuestView ? <UserRound /> : <Store />}
                    </button>
                    {canUpdate && !isGuestView && (
                        <button
                            className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                            onClick={() => navigate(`/admin/services/${id}/edit`)}
                        >
                            <Pencil /> Chỉnh sửa
                        </button>
                    )}
                    {canDelete && !isGuestView && (
                        <button
                            className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                            onClick={handleDeleteService}
                        >
                            <Trash2 /> Xoá
                        </button>
                    )}
                </div>
            </div>

            <div className={styles.layout}>
                {/* ===== CỘT TRÁI ===== */}
                <div className={styles.layoutLeft}>
                    {/* Service Info */}
                    <div className={styles.card}>
                        <div className={styles.cardBody}>
                            <div className={styles.serviceHeader}>
                                {serviceData.urlImg ? (
                                    <img
                                        src={serviceData.urlImg}
                                        alt={serviceData.name}
                                        className={`${styles.serviceImg}${isGuestView ? ` ${styles.serviceImgZoomable}` : ''}`}
                                        onClick={isGuestView ? () => setShowImagePreview(true) : undefined}
                                        title={isGuestView ? 'Bấm để phóng to' : undefined}
                                    />
                                ) : (
                                    <div className={styles.serviceImgPlaceholder}>
                                        <ImageIcon />
                                    </div>
                                )}
                                <div className={styles.serviceMainInfo}>
                                    <div className={styles.serviceName}>{serviceData.name}</div>
                                    {serviceData.code && !isGuestView && (
                                        <div className={styles.serviceCode}>Mã: {serviceData.code}</div>
                                    )}
                                    <div className={styles.serviceMeta}>
                                        <span className={`${styles.badge} ${styles.badgeBlue}`}>
                                            {TYPE_ICONS[serviceData.typeService]} {TYPE_LABELS[serviceData.typeService]}
                                        </span>
                                        {serviceData.isWeb && !isGuestView && (
                                            <span className={`${styles.badge} ${styles.badgeGreen}`}>
                                                <Globe size={12} /> Website
                                            </span>
                                        )}
                                        {!isCombo && variants.length > 1 && (
                                            <span className={`${styles.badge} ${styles.badgeGray}`}>
                                                {variants.length} biến thể
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {(!isGuestView || serviceData.description) && (
                                <div className={styles.infoGrid}>
                                    <div className={`${styles.infoRow} ${styles.infoFull}`}>
                                        <span className={styles.infoLabel}>Mô tả</span>
                                        <span className={styles.infoValue}>{serviceData.description || '—'}</span>
                                    </div>
                                    {!isGuestView && (
                                        <>
                                            <div className={styles.infoRow}>
                                                <span className={styles.infoLabel}>Vị trí trưng bày</span>
                                                <span className={styles.infoValue}>
                                                    {serviceData.displayLocation || '—'}
                                                </span>
                                            </div>
                                            <div className={styles.infoRow}>
                                                <span className={styles.infoLabel}>Ghi chú nội bộ</span>
                                                <span className={styles.infoValue}>
                                                    {serviceData.internalNote || '—'}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Variant Detail — không hiện cho combo */}
                    {!isCombo && selectedVariant && (
                        <div className={styles.card}>
                            {/* Variant tabs */}
                            {variants.length > 1 && (
                                <div className={styles.variantTabs}>
                                    {variants.map((v, idx) => (
                                        <button
                                            key={v.id}
                                            className={`${styles.variantTab}${idx === selectedVariantIdx ? ` ${styles.variantTabActive}` : ''}`}
                                            onClick={() => setSelectedVariantIdx(idx)}
                                        >
                                            {getVariantDisplayName(v)}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className={styles.cardHeader}>
                                <span className={styles.cardTitle}>
                                    {variants.length > 1
                                        ? `Biến thể: ${getVariantDisplayName(selectedVariant)}`
                                        : 'Thông tin biến thể'}
                                </span>
                                {canDelete && !isGuestView && variants.length > 1 && (
                                    <button
                                        className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                                        onClick={() => handleDeleteVariant(selectedVariant.id)}
                                        style={{ padding: '6px 12px', fontSize: 13 }}
                                    >
                                        <Trash2 size={14} /> Xoá
                                    </button>
                                )}
                            </div>

                            <div className={styles.cardBody}>
                                {/* Thông tin biến thể — giống app: SKU, Danh mục, Thuộc tính, Hoa hồng, Barcode */}
                                <div className={styles.infoGrid}>
                                    {!isGuestView && (
                                        <>
                                            <div className={styles.infoRow}>
                                                <span className={styles.infoLabel}>Mã SKU</span>
                                                <span className={styles.infoValue}>{selectedVariant.sku || '—'}</span>
                                            </div>
                                            <div className={styles.infoRow}>
                                                <span className={styles.infoLabel}>Danh mục</span>
                                                <span className={styles.infoValue}>
                                                    {TYPE_LABELS[serviceData.typeService] || '—'}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    <div className={`${styles.infoRow} ${styles.infoFull}`}>
                                        <span className={styles.infoLabel}>Thuộc tính</span>
                                        <span className={styles.infoValue}>
                                            {getVariantAttributeText(selectedVariant)}
                                        </span>
                                    </div>
                                    {!isGuestView && (
                                        <>
                                            <div className={styles.infoRow}>
                                                <span className={styles.infoLabel}>Loại hoa hồng</span>
                                                <div className={styles.commissionRow}>
                                                    <span className={styles.infoValue}>
                                                        {getCommissionDisplayName(selectedVariant.commissionTypeId)}
                                                    </span>
                                                    {canUpdate && (
                                                        <button
                                                            type="button"
                                                            className={styles.commissionEditBtn}
                                                            onClick={openCommissionModal}
                                                            title="Cập nhật hoa hồng"
                                                        >
                                                            <Wallet size={14} /> Đổi
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className={styles.infoRow}>
                                                <span className={styles.infoLabel}>Mã vạch (Barcode)</span>
                                                <span className={styles.infoValue}>
                                                    {selectedVariant.barcode || '—'}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoLabel}>Đơn vị</span>
                                        <span className={styles.infoValue}>{selectedVariant.unit || '—'}</span>
                                    </div>
                                    {isDichvu && (
                                        <div className={styles.infoRow}>
                                            <span className={styles.infoLabel}>Thời gian phục vụ</span>
                                            <span className={styles.infoValue}>
                                                {selectedVariant.durationMinutes
                                                    ? `${selectedVariant.durationMinutes} phút`
                                                    : '—'}
                                            </span>
                                        </div>
                                    )}
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoLabel}>Trạng thái</span>
                                        <span
                                            className={`${styles.badge} ${selectedVariant.inStock !== false ? styles.badgeGreen : styles.badgeRed}`}
                                        >
                                            {selectedVariant.inStock !== false ? 'Còn hàng' : 'Hết hàng'}
                                        </span>
                                    </div>
                                </div>

                                {/* Giá — giống app: Giá bán, Giá KM, Giá nhập */}
                                <div
                                    className={`${styles.priceGrid}${isGuestView ? ` ${styles.priceGridTwo}` : ''}`}
                                    style={{ marginTop: 20 }}
                                >
                                    <div className={styles.priceCard}>
                                        <span className={styles.priceCardLabel}>Giá bán</span>
                                        <span className={styles.priceCardValue}>
                                            {formatCurrency(selectedVariant.retailPrice)}
                                        </span>
                                    </div>
                                    <div className={styles.priceCard}>
                                        <span className={styles.priceCardLabel}>Giá khuyến mãi</span>
                                        <span className={`${styles.priceCardValue} ${styles.priceCardSale}`}>
                                            {Number(selectedVariant.salePrice) > 0
                                                ? formatCurrency(selectedVariant.salePrice)
                                                : 'Chưa có'}
                                        </span>
                                    </div>
                                    {!isGuestView && (
                                        <div className={styles.priceCard}>
                                            <span className={styles.priceCardLabel}>Giá nhập cuối</span>
                                            <span className={`${styles.priceCardValue} ${styles.priceCardImport}`}>
                                                {formatCurrency(selectedVariant.importPrice)}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Stock — khác nhau giữa dichvu vs sanpham */}
                                {isDichvu && !isGuestView && (
                                    <div className={styles.stockGrid} style={{ gridTemplateColumns: '1fr' }}>
                                        <div className={styles.stockCard}>
                                            <div className={styles.stockLabel}>Số lượt sử dụng</div>
                                            <div className={styles.stockValue}>{selectedVariant.usedNumber || 0}</div>
                                        </div>
                                    </div>
                                )}

                                {isProduct && !isGuestView && (
                                    <div className={styles.stockGrid}>
                                        <div className={styles.stockCard}>
                                            <div className={styles.stockLabel}>Có thể bán</div>
                                            <div className={styles.stockValue}>
                                                {(selectedVariant.quantity || 0) - (selectedVariant.notSell || 0)}
                                            </div>
                                        </div>
                                        <div className={styles.stockCard}>
                                            <div className={styles.stockLabel}>Tồn kho</div>
                                            <div className={styles.stockValue}>{selectedVariant.quantity || 0}</div>
                                        </div>
                                        <div className={styles.stockCard}>
                                            <div className={styles.stockLabel}>Không bán</div>
                                            <div className={styles.stockValue}>{selectedVariant.notSell || 0}</div>
                                        </div>
                                        <div className={styles.stockCard}>
                                            <div className={styles.stockLabel}>Cảnh báo tồn</div>
                                            <div className={styles.stockValue}>{selectedVariant.alert || 0}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Combo items */}
                    {isCombo && comboItems.length > 0 && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardTitle}>Trong combo ({comboItems.length})</span>
                            </div>
                            <div className={styles.cardBody}>
                                {comboItems.map((ci, idx) => (
                                    <div key={idx} className={styles.comboItem}>
                                        <div className={styles.comboItemInfo}>
                                            <div className={styles.comboItemName}>{ci.serviceName}</div>
                                            {ci.variantName && (
                                                <div className={styles.comboItemVariant}>{ci.variantName}</div>
                                            )}
                                        </div>
                                        <span className={styles.comboItemQty}>×{ci.quantity}</span>
                                        <span className={styles.comboItemPrice}>
                                            {formatCurrency(Number(ci.price) * ci.quantity)}
                                        </span>
                                    </div>
                                ))}
                                <div className={styles.comboTotal}>
                                    <span className={styles.comboTotalLabel}>Giá combo</span>
                                    <span className={styles.comboTotalValue}>{formatCurrency(serviceData.price)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Nút tạo biến thể mới */}
                    {!isCombo && canCreate && !isGuestView && (
                        <button
                            className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                            onClick={() => navigate(`/admin/services/${id}/create-variant`)}
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            <Plus /> Tạo biến thể mới
                        </button>
                    )}
                </div>

                {/* ===== CỘT PHẢI ===== */}
                <div className={styles.layoutRight}>
                    {/* Tổng quan — ẩn ở chế độ khách */}
                    {!isGuestView && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardTitle}>Tổng quan</span>
                            </div>
                            <div className={styles.cardBody}>
                                <div className={styles.infoRow} style={{ marginBottom: 16 }}>
                                    <span className={styles.infoLabel}>Loại</span>
                                    <span className={`${styles.badge} ${styles.badgeBlue}`}>
                                        {TYPE_ICONS[serviceData.typeService]} {TYPE_LABELS[serviceData.typeService]}
                                    </span>
                                </div>
                                {serviceData.code && (
                                    <div className={styles.infoRow} style={{ marginBottom: 16 }}>
                                        <span className={styles.infoLabel}>Mã</span>
                                        <span className={styles.infoValue}>{serviceData.code}</span>
                                    </div>
                                )}
                                <div className={styles.infoRow} style={{ marginBottom: 16 }}>
                                    <span className={styles.infoLabel}>Website</span>
                                    <span
                                        className={`${styles.badge} ${serviceData.isWeb ? styles.badgeGreen : styles.badgeGray}`}
                                    >
                                        {serviceData.isWeb ? 'Đang hiển thị' : 'Không hiển thị'}
                                    </span>
                                </div>
                                {!isCombo && (
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoLabel}>Số biến thể</span>
                                        <span className={styles.infoValue}>{variants.length}</span>
                                    </div>
                                )}
                                {isCombo && (
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoLabel}>Số mục</span>
                                        <span className={styles.infoValue}>{comboItems.length}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Giá biến thể đang chọn — cho DV/SP */}
                    {!isCombo && selectedVariant && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardTitle}>
                                    {variants.length > 1 ? getVariantDisplayName(selectedVariant) : 'Giá'}
                                </span>
                            </div>
                            <div className={styles.cardBody}>
                                <div className={styles.infoRow} style={{ marginBottom: 14 }}>
                                    <span className={styles.infoLabel}>Giá bán</span>
                                    <span className={styles.infoValueMoney}>
                                        {formatCurrency(selectedVariant.retailPrice)}
                                    </span>
                                </div>
                                {Number(selectedVariant.salePrice) > 0 && (
                                    <div className={styles.infoRow} style={{ marginBottom: 14 }}>
                                        <span className={styles.infoLabel}>Giá khuyến mãi</span>
                                        <span className={styles.infoValueMoney} style={{ color: '#EF4444' }}>
                                            {formatCurrency(selectedVariant.salePrice)}
                                        </span>
                                    </div>
                                )}
                                {!isGuestView && (
                                    <div className={styles.infoRow} style={{ marginBottom: 14 }}>
                                        <span className={styles.infoLabel}>Giá nhập</span>
                                        <span className={styles.infoValue}>
                                            {formatCurrency(selectedVariant.importPrice)}
                                        </span>
                                    </div>
                                )}
                                {!isGuestView && (
                                    <div className={styles.infoRow} style={{ marginBottom: 14 }}>
                                        <span className={styles.infoLabel}>Hoa hồng</span>
                                        <span className={styles.infoValue}>
                                            {getCommissionDisplayName(selectedVariant.commissionTypeId)}
                                        </span>
                                    </div>
                                )}
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Trạng thái</span>
                                    <span
                                        className={`${styles.badge} ${selectedVariant.inStock !== false ? styles.badgeGreen : styles.badgeRed}`}
                                    >
                                        {selectedVariant.inStock !== false ? 'Còn hàng' : 'Hết hàng'}
                                    </span>
                                </div>
                                {isProduct && !isGuestView && (
                                    <div className={styles.infoRow} style={{ marginTop: 14 }}>
                                        <span className={styles.infoLabel}>Tồn kho</span>
                                        <span className={styles.infoValue}>
                                            {(selectedVariant.quantity || 0) - (selectedVariant.notSell || 0)} có thể
                                            bán / {selectedVariant.quantity || 0} tổng
                                        </span>
                                    </div>
                                )}
                                {isDichvu && (
                                    <>
                                        <div className={styles.infoRow} style={{ marginTop: 14 }}>
                                            <span className={styles.infoLabel}>Thời gian phục vụ</span>
                                            <span className={styles.infoValue}>
                                                {selectedVariant.durationMinutes
                                                    ? `${selectedVariant.durationMinutes} phút`
                                                    : '—'}
                                            </span>
                                        </div>
                                        {!isGuestView && (
                                            <div className={styles.infoRow} style={{ marginTop: 14 }}>
                                                <span className={styles.infoLabel}>Lượt sử dụng</span>
                                                <span className={styles.infoValue}>
                                                    {selectedVariant.usedNumber || 0}
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tổng giá combo */}
                    {isCombo && comboItems.length > 0 && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardTitle}>Giá combo</span>
                            </div>
                            <div className={styles.cardBody}>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Giá combo</span>
                                    <span className={styles.infoValueMoney}>{formatCurrency(serviceData.price)}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal cập nhật hoa hồng — giống app */}
            {showCommissionModal && selectedVariant && (
                <div className={styles.modalOverlay} onClick={closeCommissionModal}>
                    <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <span className={styles.modalTitle}>Cập nhật hoa hồng</span>
                            <button type="button" className={styles.modalClose} onClick={closeCommissionModal}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.modalSubtitle}>
                                Biến thể: <strong>{getVariantDisplayName(selectedVariant)}</strong>
                            </div>

                            {variants.length > 1 && (
                                <button
                                    type="button"
                                    className={`${styles.checkRow}${applyToAllVariants ? ` ${styles.checkRowActive}` : ''}`}
                                    onClick={() => setApplyToAllVariants((v) => !v)}
                                >
                                    <span
                                        className={`${styles.checkBox}${applyToAllVariants ? ` ${styles.checkBoxActive}` : ''}`}
                                    >
                                        {applyToAllVariants && '✓'}
                                    </span>
                                    Áp dụng cho tất cả biến thể khác
                                </button>
                            )}

                            <button
                                type="button"
                                className={`${styles.commissionOption}${selectedCommissionId === 'none' ? ` ${styles.commissionOptionActive}` : ''}`}
                                onClick={() => setSelectedCommissionId('none')}
                            >
                                <div className={styles.commissionOptionTop}>
                                    <span className={styles.commissionOptionName}>Không hoa hồng</span>
                                    <span className={styles.commissionOptionValue}>—</span>
                                </div>
                                <span className={styles.commissionOptionDesc}>Bỏ áp dụng hoa hồng cho biến thể</span>
                            </button>

                            <div className={styles.commissionList}>
                                {sortedCommissions.map((commission) => (
                                    <button
                                        type="button"
                                        key={commission.id}
                                        className={`${styles.commissionOption}${selectedCommissionId === commission.id ? ` ${styles.commissionOptionActive}` : ''}`}
                                        onClick={() => setSelectedCommissionId(commission.id)}
                                    >
                                        <div className={styles.commissionOptionTop}>
                                            <span className={styles.commissionOptionName}>{commission.name}</span>
                                            <span className={styles.commissionOptionValue}>
                                                {commission.type === 'percent'
                                                    ? `${Number(commission.value)}%`
                                                    : formatCurrency(Number(commission.value))}
                                            </span>
                                        </div>
                                        {commission.description && (
                                            <span className={styles.commissionOptionDesc}>
                                                {commission.description}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.modalCancelBtn}
                                onClick={closeCommissionModal}
                                disabled={commissionSubmitting}
                            >
                                Hủy
                            </button>
                            <button
                                type="button"
                                className={styles.modalConfirmBtn}
                                onClick={handleUpdateCommission}
                                disabled={commissionSubmitting}
                            >
                                {commissionSubmitting ? 'Đang cập nhật...' : 'Cập nhật'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ảnh phóng to — chế độ khách */}
            {showImagePreview && serviceData.urlImg && (
                <div className={styles.imagePreviewOverlay} onClick={() => setShowImagePreview(false)}>
                    <button
                        type="button"
                        className={styles.imagePreviewClose}
                        onClick={() => setShowImagePreview(false)}
                    >
                        <X size={22} />
                    </button>
                    <img
                        src={serviceData.urlImg}
                        alt={serviceData.name}
                        className={styles.imagePreviewImg}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            {toastMsg && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div
                            className={styles.successIcon}
                            style={toastMsg.type === 'error' ? { background: '#EF4444' } : undefined}
                        >
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span className={styles.successText}>{toastMsg.text}</span>
                    </div>
                </div>
            )}

            {showDeleteSuccess && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div className={styles.successIcon}>✓</div>
                        <span className={styles.successText}>Xoá thành công</span>
                    </div>
                </div>
            )}
        </div>
    );
}
